// @core/stores/project/ProjectItemStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  AddItemRequest,
  BulkMoveItemsRequest,
  ItemResponse,
  MoveItemRequest,
  ProjectItem,
  UpdateItemRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectItemStore {
  // List state
  items: ProjectItem[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ProjectItem | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ProjectItem>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ProjectItem | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ProjectItem | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add item to project
   */
  async AddItem(project_id: string, data: AddItemRequest): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.AddItem(project_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Bulk move items
   */
  async BulkMoveItems(project_id: string, data: BulkMoveItemsRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.BulkMoveItems(project_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get project item
   */
  async GetItem(project_id: string, item_id: string): Promise<ProjectItem> {
    // Check cache first
    const cached = this.cache.get(item_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetItem(project_id, item_id);
      const item = response.item;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get item by key
   */
  async GetItemByKey(project_id: string, item_key: string): Promise<ProjectItem> {
    // Check cache first
    const cached = this.cache.get(item_key);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetItemByKey(project_id, item_key);
      const item = response.item;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List project items
   */
  async ListItems(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListItems(project_id, params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Move item on board
   */
  async MoveItem(project_id: string, item_id: string, data: MoveItemRequest): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.MoveItem(project_id, item_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove item from project
   */
  async RemoveItem(project_id: string, item_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.RemoveItem(project_id, item_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve item key
   */
  async ResolveItemKey(item_key: string): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.ResolveItemKey(item_key);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update project item
   */
  async UpdateItem(project_id: string, item_id: string, data: UpdateItemRequest): Promise<ProjectItem> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateItem(project_id, item_id, data);
      const item = response.item;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectItemStore = new ProjectItemStore();
